/**
 * Submit lifecycle for `CancelBookingDialog`, split out like
 * `useRefundBookingSubmit`. No receipt state — a cancellation produces nothing
 * to show, so the dialog closes on success.
 */
import { useRef, useState } from 'react';

import { cancelBooking, CancelBookingError } from '../../api/bookingCancel';
import type { BookingDetail } from '../../api/bookingsV2';
import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';

export interface CancelBookingSubmit {
  reason: string;
  setReason: (v: string) => void;
  reasonRef: React.RefObject<HTMLTextAreaElement>;
  submitting: boolean;
  error: string | null;
  canSubmit: boolean;
  submit: () => Promise<void>;
  dismiss: () => void;
}

export function useCancelBookingSubmit(
  booking: BookingDetail,
  onClose: () => void,
  onCancelled: () => void,
): CancelBookingSubmit {
  const [reason, setReasonState] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  // Suppressed while in flight: dismissing would hide the answer, not cancel it.
  useDialogKeyboard(onClose, reasonRef, submitting);

  const setReason = (v: string) => {
    setReasonState(v);
    if (error) setError(null);
  };

  const canSubmit = reason.trim().length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) {
      setError('Please say why this booking is being cancelled.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await cancelBooking(booking.id, reason.trim());
      onCancelled();
      onClose();
    } catch (err) {
      setError(
        err instanceof CancelBookingError
          ? err.message
          : 'Could not cancel this booking. Please try again.',
      );
    } finally {
      // Always: if `onCancelled` throws, the dialog stays mounted and a stuck
      // `submitting` would leave a dead form.
      setSubmitting(false);
    }
  };

  const dismiss = () => {
    if (!submitting) onClose();
  };

  return { reason, setReason, reasonRef, submitting, error, canSubmit, submit, dismiss };
}
